import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormSpy } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { getIsConnectionFailedMessageVisible, getLogin } from '../selectors/login';
import { openForgotPasswordModal } from '../actions/auth';
import { loginAccount } from '../actions/login';
import { composeValidators, required, email } from '../helpers/formValidators';

import { FormLabel, FormInput, FormFeedback, BaseForm } from './form';
import { PrimaryButton } from './buttons';

const Login = props => {
  const {
    isLoading, onOpenForgotPassword, handleOpenSignUpModal,
    closeLoginModal, connectionFailed, isIncorrectPasswordMessageVisible,
    location, loginAccount
  } = props;

  const { t } = useTranslation();
  const [isReferred, setIsReferred] = useState(false);

  const handleLogin = useCallback((values) => {
    
    loginAccount({
      email: values.email.trim(),
      password: values.password.trim()
    });
  }, [loginAccount]);

  useEffect(() => {
    const hasReferral = !!(location?.state?.from?.state?.isReferred);

    if (hasReferral && !isReferred) {
      setIsReferred(true);
    }
  }, [location, isReferred]); 

  return (
    <div>
      <div className="page-header"><h3>{t('login.title', 'Log In')}</h3></div>
      <div className="login-container container">
        <BaseForm className="login-form" onSubmit={handleLogin}>
          <FormSpy subscription={{ values: true, valid: true, submitting: true, errors: true }}>
            {({ valid, errors, submitting }) => {
              return null;
            }}
          </FormSpy>

          <p>
            {t('login.introText', "Log in with your account. Don't have an account")}
            &nbsp;
            <button type="button" className="text-link" onClick={() => { closeLoginModal(); handleOpenSignUpModal(); }}>
              {t('login.signUpLink', 'Sign Up')}
            </button>
          </p>

          {isReferred && <FormFeedback positive>{t('login.messageAuthRequired')}</FormFeedback>}
          
          <FormLabel htmlFor="login.email">Email</FormLabel>
          <FormInput 
            disabled={isLoading} 
            id="login.email" 
            validate={composeValidators(required, email)} 
            name="email" 
            type="email" 
          />

          <FormLabel htmlFor="login.password">Password</FormLabel>
          <FormInput 
            disabled={isLoading} 
            id="login.password" 
            name="password" 
            type="password" 
            validate={composeValidators(required)} 
          />

          {isIncorrectPasswordMessageVisible && (
            <FormFeedback positive={false}>
              E-mail or password is incorrect
            </FormFeedback>
          )}

          {connectionFailed && (
            <FormFeedback positive={false}>No internet connection</FormFeedback>
          )}

          <div className="login-container__footer">
            <FormSpy subscription={{ submitting: true, pristine: true, invalid: true }}>
              {({ submitting, pristine, invalid }) => (
                <PrimaryButton 
                  type="submit" 
                  label={submitting ? "Logging in..." : "Log In"} 
                  disabled={submitting || connectionFailed || pristine || invalid} 
                />
              )}
            </FormSpy>

            <button 
              className="text-link login__forgot-password-button" 
              type="button" 
              onClick={onOpenForgotPassword}
            >
              Forgot Your Password?
            </button>
          </div>
        </BaseForm>
      </div>
    </div>
  );
};

Login.propTypes = {
  isIncorrectPasswordMessageVisible: PropTypes.bool,
  isLoading: PropTypes.bool,
  connectionFailed: PropTypes.bool,
  onOpenForgotPassword: PropTypes.func,
  closeLoginModal: PropTypes.func,
  handleOpenSignUpModal: PropTypes.func,
  loginAccount: PropTypes.func,
  location: PropTypes.object
};

const mapStateToProps = state => ({
  connectionFailed: getIsConnectionFailedMessageVisible(state),
  ...getLogin(state)
});

const mapDispatchToProps = dispatch => bindActionCreators({ loginAccount, openForgotPasswordModal }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Login);
